import { Copy, Upload } from 'lucide-react';
import { useState, type ChangeEvent } from 'react';

type MediaUploaderProps = {
  onUpload: (file: File) => Promise<string>;
  onAddToGallery: (image: { src: string; alt: string }) => void;
  disabled?: boolean;
};

export default function MediaUploader({ onUpload, onAddToGallery, disabled = false }: MediaUploaderProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [message, setMessage] = useState('');

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      setIsUploading(true);
      setMessage('');
      const url = await onUpload(file);
      setUploadedUrl(url);
      setAltText(file.name.replace(/\.[^/.]+$/, '').replace(/[-_]+/g, ' '));
      setMessage('Image uploaded. You can copy the URL or add it straight to the gallery below.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleAddToGallery = () => {
    if (!uploadedUrl) {
      return;
    }

    onAddToGallery({
      src: uploadedUrl,
      alt: altText.trim() || 'Gallery image',
    });
    setMessage('Image added to the gallery draft. Click Save CMS Content to publish it.');
  };

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#33b300]">Media Upload</p>
      <p className="mt-3 text-sm leading-7 text-slate-600">
        Upload a new image to Supabase Storage, then paste the returned URL into the hero, service, gallery, or about image fields.
      </p>

      <label className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-[#33b300] hover:text-[#0d4f12]">
        <Upload size={18} />
        {isUploading ? 'Uploading...' : 'Choose Image'}
        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" disabled={disabled || isUploading} />
      </label>

      {uploadedUrl ? (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Uploaded URL</p>
          <div className="mt-3 flex flex-col gap-3 md:flex-row">
            <input
              readOnly
              value={uploadedUrl}
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none"
            />
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(uploadedUrl)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0d4f12] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#0a3c0e]"
            >
              <Copy size={16} /> Copy URL
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
            <input
              value={altText}
              onChange={(event) => setAltText(event.target.value)}
              placeholder="Describe this image for the gallery"
              className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-[#33b300]"
            />
            <button
              type="button"
              onClick={handleAddToGallery}
              className="inline-flex items-center justify-center rounded-full bg-[#33b300] px-5 py-3 text-sm font-bold text-white transition hover:bg-[#289000]"
            >
              Add To Gallery
            </button>
          </div>
          <img src={uploadedUrl} alt="Uploaded preview" className="mt-4 h-48 w-full rounded-2xl object-cover" />
        </div>
      ) : null}

      {message ? <p className="mt-4 text-sm font-semibold text-slate-700">{message}</p> : null}
    </div>
  );
}
